import { computed } from "vue";
import { useNuxtApp, useState } from "#imports";
import { normalizeMediaUrl } from "~/utils/normalizeMediaUrl";

type OneCNewsItem = {
  Код?: string;
  ДатаНовости?: string;
  Заголовок?: string;
  Содержание?: string;
  Банер?: string | number | boolean;
  ПутьКФайлу?: string;
  Карусель?: string | number | boolean;
  URL?: string;
};

export type HomeAnnouncement = {
  id: string;
  date: string;
  title: string;
  body: string;
};

export type HomeBanner = {
  id: string;
  title: string;
  image: string;
  link?: string;
};

export type PostLoginBanner = {
  id: string;
  date: string;
  title: string;
  image: string;
  link?: string;
  body: string;
};

let newsLoadPromise: Promise<void> | null = null;

const isTruthyFlag = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();

  return normalized === "1" || normalized === "true" || normalized === "yes";
};

export const useHomeNews = () => {
  const rawItems = useState<OneCNewsItem[]>("home-news:items", () => []);
  const loading = useState<boolean>("home-news:loading", () => false);
  const loaded = useState<boolean>("home-news:loaded", () => false);

  const nuxtApp = useNuxtApp();
  const $useOneCApi = nuxtApp.$useOneCApi as
    | ((action: string, payload?: Record<string, any>) => Promise<any>)
    | undefined;

  const loadNews = async (force = false) => {
    if (!process.client) return rawItems.value;

    if (!force && loaded.value) {
      return rawItems.value;
    }

    if (newsLoadPromise) {
      await newsLoadPromise;
      return rawItems.value;
    }

    newsLoadPromise = (async () => {
      loading.value = true;
      try {
        if (typeof $useOneCApi !== "function") return;

        const response = await $useOneCApi("getNews", {});
        rawItems.value = Array.isArray(response) ? response : [];
        loaded.value = true;
      } catch (error) {
        console.error("Error fetching news", error);
      } finally {
        loading.value = false;
        newsLoadPromise = null;
      }
    })();

    await newsLoadPromise;
    return rawItems.value;
  };

  const announcements = computed<HomeAnnouncement[]>(() => {
    return rawItems.value
      .filter((item) => String(item?.Содержание ?? "").trim())
      .map((item, index) => ({
        id: String(item?.Код ?? `${item?.ДатаНовости ?? ""}-${index}`),
        date: String(item?.ДатаНовости ?? "").trim(),
        title: String(item?.Заголовок ?? "").trim(),
        body: String(item?.Содержание ?? "").trim(),
      }));
  });

  const banners = computed<HomeBanner[]>(() => {
    return rawItems.value
      .filter((item) => isTruthyFlag(item?.Карусель))
      .map((item, index) => {
        const image = normalizeMediaUrl(item?.ПутьКФайлу);
        if (!image) return null;

        const link = String(item?.URL ?? "").trim();
        return {
          id: String(item?.Код ?? `banner-${index}`),
          title: String(item?.Заголовок ?? "").trim(),
          image,
          link: link || undefined,
        };
      })
      .filter((item): item is HomeBanner => Boolean(item));
  });

  const postLoginBanners = computed<PostLoginBanner[]>(() => {
    return rawItems.value
      .filter((item) => isTruthyFlag(item?.Банер))
      .map((item, index) => {
        const image = normalizeMediaUrl(item?.ПутьКФайлу);
        if (!image) return null;

        const link = String(item?.URL ?? "").trim();
        return {
          id: String(item?.Код ?? `post-login-banner-${index}`),
          date: String(item?.ДатаНовости ?? "").trim(),
          title: String(item?.Заголовок ?? "").trim(),
          image,
          link: link || undefined,
          body: String(item?.Содержание ?? "").trim(),
        };
      })
      .filter((item): item is PostLoginBanner => Boolean(item));
  });

  return {
    rawItems,
    loading,
    announcements,
    banners,
    postLoginBanners,
    loadNews,
  };
};
